import * as Card from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { useNavigate } from '@solidjs/router'
import { userId } from '@stateApp'
import { For, Show, createEffect, createSignal, on, onCleanup, onMount } from 'solid-js'
import { useService } from 'solid-services'
import { Box, Flex } from 'styled-system/jsx'
import { useChannel, useUi } from '../state'
import Item from './Item'
import MessageActions from './MessageActions'
import TypingIndicator from './TypingIndicator'

// -----------------------------------------------------------------------------
// 1. THE GATEKEEPER (Parent)
// -----------------------------------------------------------------------------
export function Messages(props: { isMinimal?: boolean }) {
  const { activeChannelId, historyQuery, focusMessageId } = useService(useChannel)()

  return (
    <>
      {/*
        KEYED MOUNT: By keying on the channel ID AND focus ID, SolidJS physically
        destroys and recreates the MessagesList when either changes.
        This guarantees zero state leakage (scroll positions, observers, etc.) between views.
      */}
      <Show when={`${activeChannelId()}-${focusMessageId() || 'latest'}`} keyed>
        {(instanceKey) => (
          <Show
            // GATED MOUNT: Do not render the DOM until TanStack has finished
            // the initial fetch for this specific query key.
            when={!historyQuery.isLoading}
            fallback={<LoadingChat />}
          >
            <MessagesList isMinimal={props.isMinimal} />
          </Show>
        )}
      </Show>
    </>
  )
}

// -----------------------------------------------------------------------------
// 2. THE DUMB CHILD (MessagesList)
// -----------------------------------------------------------------------------
function MessagesList(props: { isMinimal?: boolean }) {
  const navigate = useNavigate()
  const { timeline, focusMessageId, historyQuery, scrollTo, chatMode, setNotAtBottom } = useService(useChannel)()
  const { channelPathRoot } = useService(useUi)()

  const [container, setContainer] = createSignal<Element | null>(null)
  const [topSentinel, setTopSentinel] = createSignal<HTMLDivElement | null>(null)
  const [bottomSentinel, setBottomSentinel] = createSignal<HTMLDivElement | null>(null)

  // These no longer need cleanup on channel switch because the component is destroyed.
  let previousScrollHeight = 0
  let fetchingDirection: 'older' | 'newer' | null = null

  onMount(() => {
    const root = container()

    // Give SolidJS and the browser 50ms to completely finish
    // evaluating the <For> loop and painting the DOM nodes.
    setTimeout(() => {
      const currentTimeline = timeline()

      if (root && currentTimeline.length > 0) {
        const focusId = focusMessageId()

        if (focusId) {
          const el = document.getElementById(focusId)
          if (el) {
            el.scrollIntoView({ block: 'center' })
            console.log(`🎯 Successfully scrolled to ${focusId}`)
          } else {
            console.warn(`⚠️ [Chat] Focused message ${focusId} not found in DOM!`)
          }
        } else {
          root.scrollTop = root.scrollHeight
        }
      }
    }, 50)
  })

  // 2. The Intersection Observer
  createEffect(() => {
    const root = container()
    const top = topSentinel()
    const bottom = bottomSentinel()

    if (!root || !top || !bottom) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // TOP: Fetch Older
          if (entry.target === top && entry.isIntersecting) {
            if (historyQuery.hasNextPage && !historyQuery.isFetchingNextPage) {
              previousScrollHeight = root.scrollHeight
              fetchingDirection = 'older'
              historyQuery.fetchNextPage()
            }
          }

          // BOTTOM: Fetch Newer
          if (entry.target === bottom) {
            setNotAtBottom(!entry.isIntersecting)

            if (
              entry.isIntersecting &&
              chatMode() === 'history' &&
              historyQuery.hasPreviousPage &&
              !historyQuery.isFetchingPreviousPage
            ) {
              fetchingDirection = 'newer'
              historyQuery.fetchPreviousPage()
            }
          }
        }
      },
      {
        root,
        rootMargin: '20px 0px',
        threshold: 0,
      }
    )

    observer.observe(top)
    observer.observe(bottom)

    onCleanup(() => observer.disconnect())
  })

  // 3. Scroll Math for Prepending Messages
  createEffect(
    on(timeline, () => {
      const root = container()
      if (!root || fetchingDirection !== 'older') return

      requestAnimationFrame(() => {
        const heightDiff = root.scrollHeight - previousScrollHeight
        if (heightDiff > 0) {
          root.scrollTop += heightDiff
        }
        fetchingDirection = null
      })
    })
  )

  // 4. External ScrollTo (e.g. Realtime incoming or manual click)
  createEffect(() => {
    const target = scrollTo()
    if (target?.id) {
      setTimeout(() => {
        const el = document.getElementById(target.id)
        if (el) el.scrollIntoView({ behavior: (target as any).behavior, block: (target as any).align as any })
      }, 50)
    }
  })

  const handleScrollToBottom = () => {
    if (chatMode() === 'history') {
      navigate(channelPathRoot())
    } else {
      const root = container()
      if (root) root.scrollTo({ top: root.scrollHeight, behavior: 'smooth' })
    }
  }

  return (
    <>
      <MessageActions scrollToBottom={handleScrollToBottom} />
      <Card.Root
        display="block"
        position="relative"
        overflowY="auto"
        height="100%"
        minHeight={0}
        ref={setContainer}
        border={props.isMinimal ? 'none' : ''}
        style={{ "overflow-anchor": "none" }}
      >
        <Show when={historyQuery.isFetchingNextPage}>
          <LoadingBar position="top" />
        </Show>

        <div ref={setTopSentinel} style={{ height: '1px', width: '100%' }} />

        <Box width="100%" px="16px" pt="14px" pb="4px" flexDirection="column">
          <For each={timeline()}>
            {(message) => {
              const isFocusingMessage = focusMessageId() === message.id
              const isHighlightMessage = scrollTo()?.id === message.id && scrollTo()?.isHighlight

              return (
                <Item
                  isMinimal={props.isMinimal}
                  isFocus={isFocusingMessage || isHighlightMessage}
                  message={message}
                  isMine={message.created_by === userId()}
                  showMessageAuthor={message._isFirstInGroup}
                  isFirstInGroup={message._isFirstInGroup}
                  isLastInGroup={message._isLastInGroup}
                />
              )
            }}
          </For>
        </Box>

        <div ref={setBottomSentinel} style={{ height: '1px', width: '100%' }} />

        <Show when={historyQuery.isFetchingPreviousPage}>
          <LoadingBar position="bottom" />
        </Show>
        <TypingIndicator />
      </Card.Root>
    </>
  )
}

// -----------------------------------------------------------------------------
// Shared Loading Bar
// -----------------------------------------------------------------------------
const LoadingBar = (props: { position: 'top' | 'bottom' }) => (
  <Box
    position="absolute"
    left="0"
    right="0"
    height="2px"
    overflow="hidden"
    zIndex="10"
    bg="bg.subtle"
    top={props.position === 'top' ? '0' : 'auto'}
    bottom={props.position === 'bottom' ? '0' : 'auto'}
  >
    <Box
      height="100%"
      width="100%"
      bg="var(--colors-jade-a6, #10b981)"
      animationName='fade-out'
      animationIterationCount={'infinite'}
      animationDuration={'1s'}
    />
  </Box>
)
const LoadingChat = () => (
  <Card.Root
    display="flex"
    w='full'
    h='full'
    justifyContent='center'
    alignItems='center'>
    <Spinner />
  </Card.Root >
)
